import { zodResolver } from "@hookform/resolvers/zod";
import React, { useMemo, useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import { ownerSummingUpFormSchema, type OwnerSummingUpFormValues } from "../model/schemas";

import type {
  OrderDetailResponseDTO,
  ParticipantAgreedWagonsSaveRequestDTO,
  ParticipantAgreedWagonsUpdateDTO,
} from "@/common/api";
import { useGetParticipantAgreedWagons, useSaveParticipantAgreedWagons } from "@/common/api";
import { Button, FormField, FormItem, FormMessage, Input, FormLabel } from "@/common/components";
import { useToast } from "@/common/hooks";

interface OwnerSummingUpFormProps {
  order: OrderDetailResponseDTO;
  isReadOnly?: boolean;
}

interface ParticipantAgreedWagonsResponse extends ParticipantAgreedWagonsSaveRequestDTO {
  tradeFinished?: boolean;
}

export const OwnerSummingUpForm: React.FC<OwnerSummingUpFormProps> = ({ order, isReadOnly }) => {
  const { toast } = useToast();

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [isFormFrozen, setIsFormFrozen] = useState(false);

  const {
    data: participantsData,
    isLoading,
    refetch: refetchParticipantsData,
  } = useGetParticipantAgreedWagons(
    { orderId: order.id! },
    {
      query: {
        enabled: !!order.id,
      },
    },
  );

  const hasAccess = Boolean(order?.isOwner);

  const participants = participantsData?.data.payload as
    | ParticipantAgreedWagonsResponse
    | undefined;
  const isTradeFinished = participants?.tradeFinished;

  const participantsArray = useMemo((): ParticipantAgreedWagonsUpdateDTO[] => {
    return participants?.participants || [];
  }, [participants]);

  const { mutateAsync: saveParticipantAgreedWagonsAsync, isPending } =
    useSaveParticipantAgreedWagons();

  const methods = useForm<OwnerSummingUpFormValues>({
    resolver: zodResolver(ownerSummingUpFormSchema),
    defaultValues: {
      participants: [],
    },
  });

  const { control, handleSubmit, reset } = methods;

  // Обновляем форму при изменении количества участников.
  // Не сбрасываем значения, если торги завершены или форма уже была отправлена,
  // чтобы сохранить введённые данные.
  useEffect(() => {
    if (isTradeFinished || isFormFrozen) {
      return;
    }

    if (participantsArray.length > 0) {
      reset({
        participants: Array.from({ length: participantsArray.length }, () => ({ wagons: null })),
      });
    } else if (!isLoading && participantsArray.length === 0) {
      reset({
        participants: [],
      });
    }
  }, [participantsArray.length, isLoading, reset, isTradeFinished, isFormFrozen]);

  const { fields } = useFieldArray({
    control,
    name: "participants",
  });

  const onSubmit = (data: OwnerSummingUpFormValues) => {
    // Преобразуем данные формы в формат API
    const participantsToUpdate: ParticipantAgreedWagonsUpdateDTO[] = data.participants
      .map((formParticipant, index) => {
        const participant = participantsArray[index];
        if (!participant) {
          return null;
        }

        const result: ParticipantAgreedWagonsUpdateDTO = {
          id: participant.id,
        };
        if (formParticipant.wagons !== null && formParticipant.wagons !== undefined) {
          result.numberAgreedWagons = formParticipant.wagons;
        }
        return result;
      })
      .filter((item): item is ParticipantAgreedWagonsUpdateDTO => item !== null);

    const requestData: ParticipantAgreedWagonsSaveRequestDTO = {
      orderId: order.id!,
      participants: participantsToUpdate,
    };

    saveParticipantAgreedWagonsAsync({ data: requestData })
      .then(() => {
        toast({
          title: "Успешно",
          description: "Итоги  отправлены",
        });
        setIsSubmitDisabled(true);
        setIsFormFrozen(true);
        refetchParticipantsData();
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка",
          description: error instanceof Error ? error.message : "Не удалось отправить итоги",
        });
      });
  };

  if (isLoading) {
    return (
      <div className="mb-6">
        <p className="text-Text-secondary">Загрузка данных...</p>
      </div>
    );
  }

  if (participantsArray.length === 0) {
    return (
      <div className="bg-[#F5F5F7] px-4 py-5 mt-8 border border-Border-gray rounded-lg overflow-hidden flex flex-col">
        <div className="p-0 md:py-3 mb-3 md:mb-6">
          <h2 className="text-xl md:text-2xl font-semibold text-Text-main leading-7">
            Подведение итогов
          </h2>
        </div>
        <p className="text-sm md:text-base text-Text-main font-medium pb-2 text-center">
          Ни один из собственников вагонов участие не принял
        </p>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={hasAccess ? handleSubmit(onSubmit) : (e) => e.preventDefault()} noValidate>
        <div className="bg-[#F5F5F7] px-4 md:px-6 py-5 mt-8 border border-Border-gray rounded-lg overflow-hidden flex flex-col">
          <div className="p-0  md:py-3 mb-3 md:mb-6">
            <h2 className="text-xl md:text-2xl font-semibold text-Text-main leading-7">
              Подведение итогов
            </h2>
            {isTradeFinished && (
              <p className="text-xs md:text-sm text-Text-secondary mt-1">Победители уже выбраны</p>
            )}
          </div>
          {!!fields.length && (
            <>
              <div className="flex gap-5 items-start md:items-end flex-col md:flex-row">
                <div className="text-sm md:text-base text-Text-main font-medium self-start md:self-center pb-2">
                  Количество вагонов согласовано:
                </div>
                {fields.map((field, index) => (
                  <FormField
                    key={field.id}
                    control={control}
                    name={`participants.${index}.wagons`}
                    render={({ field }) => (
                      <FormItem className="w-full md:w-60 md:h-24">
                        <FormLabel className="text-sm md:text-base text-Text-main font-medium mb-2">
                          Участник {index + 1}
                        </FormLabel>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            field.onChange(value === "" ? null : Number(value));
                          }}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          type="number"
                          disabled={isTradeFinished || isFormFrozen || !hasAccess}
                          min={0}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
              </div>

              {hasAccess && (
                <div className="flex justify-end mt-6">
                  <Button
                    type="submit"
                    disabled={isPending || isTradeFinished || isSubmitDisabled}
                    className="px-6 md:px-10 py-2 md:py-3 text-sm md:text-base whitespace-normal break-words h-auto md:h-10"
                  >
                    {isPending ? "Отправка..." : "Подтвердить и отправить итоги"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </form>
    </FormProvider>
  );
};
